// 1. React imports (none in this file)

// 2. Third-party library imports
import { get } from 'lodash-es';
import { all, call, delay, fork, put, takeLatest } from 'redux-saga/effects';

// 3. Local imports - Constants
import {
    API_NAME,
    maxAPIRefetchCount,
    refetchAPIDelay,
} from '../../config/config';
import { BANK_FILE_DESCRIPTORS_PAGE, DETAILS_TAB } from '../../config/tableAndPageConstants';

// 4. Local imports - Custom hooks (none in this file)

// 5. Local imports - Project-specific modules (components) (none in this file)

// 6. Local imports - Utility functions
import {
    checkShouldRequestRefetch,
    getSortFieldsWithCustomFields,
    removeAppliedFiltersForApiRequest,
} from '../../utils/commonFunctions';
import { DynamicObject } from '../../utils/commonInterfaces';

// 7. Local imports - Redux actions
import {
    createOrEditBankFileDescriptorRequestAction,
    deleteBankFileDescriptorRequestAction,
    getBankFileDescriptorDataErrorAction,
    getBankFileDescriptorDataRequestAction,
    getBankFileDescriptorDataSuccessAction,
    getBankFileDescriptorsErrorAction,
    getBankFileDescriptorsRequestAction,
    getBankFileDescriptorsSuccessAction,
    setBankFileDescriptorSelectedIdRequestAction,
    setBankFileDescriptorSelectedIdSuccessAction
} from './bankFileDescriptorSlice';

// 8. Other local modules
import type { ApplicationState } from '../storeTypes';
import { _queries as queries } from '../../graphql/queries.graphql';
import client, { delApi, post } from 'src/api';
import { BankFileDescriptor } from './types';

let refetchCount = 0;

/**
 * Function that calls the API for fetching the bank file descriptor list.
 * @param param0
 */
function* handleGetBankFileDescriptorsRequest({ payload }: any) {
    const errorMessage = 'Error fetching bank file descriptors list. Please try again later.';
    try {
        // To call async functions, use redux-saga's `call()`.
        const {
            filters,
            sortBy,
            sortAscending,
            pageSize,
            currentPage
        } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true);
        const sortObject = getSortFieldsWithCustomFields(sortBy);

        const res: DynamicObject = yield call(
            [client, 'graphql'],
            {
                query: queries.GET_BANK_FILE_DESCRIPTORS_FOR_COMPANY, 
                variables: {
                    ...cleanFilters,
                    SortField: sortBy,
                    ...sortObject,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * BANK_FILE_DESCRIPTORS_PAGE.pageSize
                }
            }
        );

        const { CustomerBankDescriptors } = get(res.data, 'GetCustomerBankDescriptorsForCompany');
        
        const responsePayload = {
            data: CustomerBankDescriptors ? CustomerBankDescriptors.map((bd: DynamicObject) => {
                return {
                    Id: bd.Customer.Id,
                    Customer: bd.Customer,
                    Descriptors: bd.BankDescriptors
                } as BankFileDescriptor
            }) : [],
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage:
                    !(CustomerBankDescriptors.length < pageSize) &&
                    !(pageSize < BANK_FILE_DESCRIPTORS_PAGE.pageSize),
            },
        };

        refetchCount = 0;
        yield put(getBankFileDescriptorsSuccessAction(responsePayload));
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(getBankFileDescriptorsRequestAction(payload));
        } else {
            yield put(getBankFileDescriptorsErrorAction([errorMessage]));
        }
    }
}

/**
 * Function calling the API for fetching the bank file descriptor data based on the given id.
 * @param param0
 */
function* handleGetBankFileDescriptorDataRequest({ payload }: any ) {
    const errorMessage =
        'Error fetching bank file descriptor details. Please try again later.';
    try {
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, 'graphql'],
            {
                query: queries.GET_CUSTOMER_BANK_FILE_DESCRIPTOR_DETAILS, 
                variables: {
                    CustomerId: payload
                }
            }
        );

        const result = get(res.data, 'GetCustomerBankDescriptorDetails');

        if (result) {
            const responsePayload = {
                record: result,
            };

            yield put(getBankFileDescriptorDataSuccessAction(responsePayload));
        } else {
            yield put(getBankFileDescriptorDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getBankFileDescriptorDataErrorAction([errorMessage]));
    }
}

/**
 * Function that sets the selected bank file descriptor id for reference.
 * @param param0
 */
function* handleSetBankFileDescriptorSelectedIdRequest({ payload }: any) {
    const { customerId, callback } = payload;
    yield put(setBankFileDescriptorSelectedIdSuccessAction(customerId));
    if (callback) callback();
}

/**
 * Function for creating the customer bank file descriptors.
 * @param param0
 */
function* handleCreateOrEditBankFileDescriptorsRequest({ payload: sagaPayload }: any) {
    const { BankFileDescriptors, callback } = sagaPayload;

    const payload = { BankFileDescriptors };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/payment/create-or-edit-bank-file-descriptor',
            options: {
                body: payload
            }
        });
        if (callback) {
            const response = {
                IsSuccess: true,
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;
        
            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }
            returnData.IsSuccess = false;
            callback(returnData);
        }
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

function* handleDeleteBankFileDescriptorRequest({ payload: sagaPayload }: any) {
    const { filter, customerIds, excludeCustomerIds, callback } =
        sagaPayload;

    const cleanFilters = removeAppliedFiltersForApiRequest(filter, true);
  
    try {
        const apiResponse: DynamicObject = yield call(delApi,
            '/payment/delete-bank-file-descriptor',
            {
                BankFileDescriptorManagementFilter: {
                    ...cleanFilters,
                    CustomerIds: customerIds,
                    ExcludeCustomerIds: excludeCustomerIds,
                }
            }
        );

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: get(apiResponse, 'Messages')
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;
        
            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }
            returnData.IsSuccess = false;
            callback(returnData);
        }
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchGetBankFileDescriptorsRequest() {
    yield takeLatest(
        getBankFileDescriptorsRequestAction.type,
        handleGetBankFileDescriptorsRequest
    );
}

function* watchGetBankFileDescriptorDataRequest() {
    yield takeLatest(
        getBankFileDescriptorDataRequestAction.type,
        handleGetBankFileDescriptorDataRequest
    );
}

function* watchSetBankFileDescriptorSelectedIdRequest() {
    yield takeLatest(
        setBankFileDescriptorSelectedIdRequestAction.type,
        handleSetBankFileDescriptorSelectedIdRequest
    );
}

function* watchCreateOrEditBankFileDescriptorsRequest() {
    yield takeLatest(
        createOrEditBankFileDescriptorRequestAction.type,
        handleCreateOrEditBankFileDescriptorsRequest
    );
}

function* watchDeleteBankFileDescriptorRequest() {
    yield takeLatest(
        deleteBankFileDescriptorRequestAction.type,
        handleDeleteBankFileDescriptorRequest
    );
}

function* bankFileDescriptorsSaga() {
    yield all([
        fork(watchGetBankFileDescriptorsRequest),
        fork(watchGetBankFileDescriptorDataRequest),
        fork(watchSetBankFileDescriptorSelectedIdRequest),
        fork(watchCreateOrEditBankFileDescriptorsRequest),
        fork(watchDeleteBankFileDescriptorRequest)
    ]);
}

export default bankFileDescriptorsSaga;